<template v-cloak>
  <ul v-if="items">
    <li v-for="item in items" v-html="item" />
  </ul>
  <p v-else-if="emptyMessage" v-html="emptyMessage" />
  <p v-if="message" :aria-live="ariaLive" class="sr-only" v-html="message" />
</template>

<script>
import { defineComponent, onMounted, onBeforeMount, ref } from 'vue';

export default defineComponent( {
  name: 'ActionPerspectives',
  props: {
    incidentId: {
      type: Number,
      required: true,
    },
    interval: {
      type: Number,
      default: 60,
    },
    originals: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Object,
      default: () => ( {
        escalating: 'Escalating, %s added',
        downscaling: 'Downscaling, %s removed',
        plural: 'action perspectives',
        singular: 'action perspective',
        empty: 'No action perspectives available.',
      } ),
    }
  },
  setup( props ) {
    const items = ref( [ ...props.originals ] );
    const isLoading = ref( false );
    const updated = ref( false );
    const ariaLive = ref( 'polite' );
    const message = ref( '' );

    const arraysAreEqual = ( arr1, arr2 ) => {
      if ( arr1.length !== arr2.length ) return false;
      for ( let i = 0; i < arr1.length; i++ ) {
        if ( arr1[ i ] !== arr2[ i ] ) return false;
      }
      return true;
    };

    /**
     * Get the new items.
     *
     * @param {array} oldItems Set of old items.
     * @param {array} newItems Set of new items.
     * @return {*}
     */
    const getNewItems = ( oldItems, newItems ) => newItems.filter( item => ! oldItems.includes( item ) );

    /**
     * Get the removed items.
     *
     * @param {array} oldItems Set of old items.
     * @param {array} newItems Set of new items.
     * @return {*}
     */
    const getRemovedItems = ( oldItems, newItems ) => oldItems.filter( item => ! newItems.includes( item ) );

    /**
     * Get singular or plural label.
     * @param {number} count
     * @return {string}
     */
    const singularOrPlural = ( count ) => {
      const { plural, singular } = props.labels;

      return count > 1 ? plural : singular;
    }

    const fetchActionPerspectives = async () => {
      if ( !! isLoading.value ) return;

      try {
        isLoading.value = true;
        message.value = '';
        const { url, route } = window.vrgz_variables.rest;
        await fetch( `${ url }${route}/incident/${ props.incidentId }` )
            .then( response => {
              if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
              }
              return response.json();
            } )
            .then( data => {
              const newItems = data.action_perspectives || [];

              if ( ! arraysAreEqual( items.value, newItems ) ) {
                const { escalating, downscaling } = props.labels;

                if ( newItems.length >= items.value.length ) {
                  // The incident has escalated or is escalating, therefor assertive.
                  ariaLive.value = 'assertive';

                  const addedItems = getNewItems( items.value, newItems );
                  message.value = `${ escalating.replace( '%s', singularOrPlural( addedItems.length ).toLowerCase() ) }: ${ addedItems.join( ', ' ) }`;
                } else {
                  // The incident is downscaling or is downscaled, therefor polite.
                  ariaLive.value = 'polite';
                  const removedItems = getRemovedItems( items.value, newItems );
                  message.value = `${ downscaling.replace( '%s', singularOrPlural( removedItems.length ).toLowerCase() ) }: ${ removedItems.join( ', ' ) }`;
                }

                items.value = newItems;
                setTimeout( () => message.value = '', 1000 );
              }
            } )
            .finally( () => isLoading.value = false )
            .catch( error => console.error( 'Error fetching comments:', error ) );
      } catch ( error ) {
        console.error( 'Error fetching comments:', error );
        message.value = '';
      }
    };

    /**
     * Fetch incidents and risks async.
     * @return {Promise<void>}
     */
    const fetchData = async () => await fetchActionPerspectives();

    onBeforeMount( fetchData );
    onMounted( () => setInterval( fetchData, props.interval * 1000 ) );

    return {
      items,
      ariaLive,
      updated,
      message,
      emptyMessage: props.labels.empty,
    };
  },
} );
</script>
